import React, { useState, useEffect, useMemo } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { GridLayout } from './Layouts/Grid';
import { TableLayout } from './Layouts/Table';
import { StackedLayout } from './Layouts/Stacked';

import useShoppingBasketCart from '@src/hooks/useShoppingBasketCart';
import useCurrentTextStyle from '@src/hooks/useCurrentTextStyle';
import useMediaQuery from '@src/hooks/useMediaQuery';
import useCartItemRemoval from '@src/hooks/useCartItemRemoval';

import { ICartItem } from '@src/types/cart';

import './CartItems.scss';
import CartUndoWishlistPopup from './CartUndoWishlistPopup';

export const CartItems = ({ settings }) => {
  const { getCurrentTextStyle } = useCurrentTextStyle();

  // Get mobile breakpoint for padding calculations
  const isMobile = useMediaQuery(null, 767);

  const { section, block_settings, cart } = settings;
  const blockSettings = block_settings || {};

  const { cartData, updateItemAndFetch, isUpdatingItem, isGlobalCartLoading } = useShoppingBasketCart();

  // Use the reusable cart item removal hook with settings
  const { tempRemovedItems, handleRemoveItem, handleUndoRemove, handleUndoWishlist } = useCartItemRemoval();

  // Default layout from block settings, fallback to section
  const productLayout = blockSettings.layout || section?.product_layout || 'stacked';

  // Extract settings that are actually used in this component
  const {
    // Header Settings (from schema)
    heading = 'My Bag',
    heading_style = 'b1',
    heading_style_mobile,
    heading_style_tablet,
    heading_style_ultrawide,
    heading_style_enable_responsive,
    heading_text_color,
    show_cart_item_count = true,
    item_count_text_style = 'b1',
    item_count_text_style_mobile,
    item_count_text_style_tablet,
    item_count_text_style_ultrawide,
    item_count_text_style_enable_responsive,
    item_count_text_color,
    header_padding_top = 0,
    header_padding_bottom = 0,
    header_padding_left = 0,
    header_padding_right = 0,
    header_padding_top_mobile = 0,
    header_padding_bottom_mobile = 0,
    header_padding_left_mobile = 0,
    header_padding_right_mobile = 0,
    empty_cart_message = 'Your cart is empty',
    empty_cart_message_style = 'b1',
    empty_cart_message_style_mobile,
    empty_cart_message_style_tablet,
    empty_cart_message_style_ultrawide,
    empty_cart_message_style_enable_responsive,
    empty_padding_top = 0,
    empty_padding_bottom = 0,
    empty_padding_left = 0,
    empty_padding_right = 0,
    empty_padding_top_mobile = 0,
    empty_padding_bottom_mobile = 0,
    empty_padding_left_mobile = 0,
    empty_padding_right_mobile = 0,
    empty_cart_message_alignment = 'text-center'
  } = blockSettings;

  // Get item count from cart or settings
  const itemCount = cartData?.item_count || 0;

  // Header padding styles
  const headerPaddingStyle = useMemo(
    () => ({
      paddingTop: `${isMobile ? header_padding_top_mobile : header_padding_top}px`,
      paddingBottom: `${isMobile ? header_padding_bottom_mobile : header_padding_bottom}px`,
      paddingLeft: `${isMobile ? header_padding_left_mobile : header_padding_left}px`,
      paddingRight: `${isMobile ? header_padding_right_mobile : header_padding_right}px`
    }),
    [
      isMobile,
      header_padding_top,
      header_padding_bottom,
      header_padding_left,
      header_padding_right,
      header_padding_top_mobile,
      header_padding_bottom_mobile,
      header_padding_left_mobile,
      header_padding_right_mobile
    ]
  );

  const emptyPaddingStyle = useMemo(
    () => ({
      paddingTop: `${isMobile ? empty_padding_top_mobile : empty_padding_top}px`,
      paddingBottom: `${isMobile ? empty_padding_bottom_mobile : empty_padding_bottom}px`,
      paddingLeft: `${isMobile ? empty_padding_left_mobile : empty_padding_left}px`,
      paddingRight: `${isMobile ? empty_padding_right_mobile : empty_padding_right}px`
    }),
    [
      isMobile,
      empty_padding_top,
      empty_padding_bottom,
      empty_padding_left,
      empty_padding_right,
      empty_padding_top_mobile,
      empty_padding_bottom_mobile,
      empty_padding_left_mobile,
      empty_padding_right_mobile
    ]
  );

  const handleQuantityChange = async (item: ICartItem, newQuantity: number) => {
    if (isUpdatingItem) return;

    if (newQuantity >= 1) {
      await updateItemAndFetch(
        {
          key: item.key,
          quantity: newQuantity
        },
        true
      );
    } else {
      await handleRemoveItem(item);
    }
  };

  const tempRemovedItemsStyle = {
    paddingLeft: isMobile ? blockSettings.item_padding_left_mobile : blockSettings.item_padding_left,
    paddingRight: isMobile ? blockSettings.item_padding_right_mobile : blockSettings.item_padding_right
  };

  return (
    <div className={`items-layout__${productLayout} overflow-hidden`}>
      {/* Header container with border but no padding */}
      <div className='border-b border-[#EDEDED]'>
        {/* Inner header with custom padding */}
        <div
          className='flex justify-start items-center gap-2'
          style={headerPaddingStyle}
        >
          <h3
            className={`m-0 ${getCurrentTextStyle(
              heading_style,
              heading_style_enable_responsive,
              heading_style_mobile,
              heading_style_tablet,
              heading_style_ultrawide
            )}`}
            style={{ color: heading_text_color }}
          >
            {heading}
          </h3>
          {show_cart_item_count && itemCount > 0 && (
            <span
              className={getCurrentTextStyle(
                item_count_text_style,
                item_count_text_style_enable_responsive,
                item_count_text_style_mobile,
                item_count_text_style_tablet,
                item_count_text_style_ultrawide
              )}
              style={{ color: item_count_text_color }}
            >
              {itemCount} items
            </span>
          )}
        </div>
      </div>

      {/* Undo notifications for temporarily removed items using CartUndoWishlistPopup */}
      <div
        className={`removed-items-notifications ${tempRemovedItems.length > 0 ? 'mt-4' : ''}`}
        style={tempRemovedItemsStyle}
      >
        <TransitionGroup className='space-y-2'>
          {tempRemovedItems.map((item) => {
            const popupRef = React.createRef<HTMLDivElement>();
            return (
              <CSSTransition
                key={item.removedAt}
                nodeRef={popupRef}
                timeout={300}
                classNames='minicart'
              >
                <div ref={popupRef}>
                  <CartUndoWishlistPopup
                    item={item}
                    settings={blockSettings}
                    onClickUndo={() => handleUndoRemove(item)}
                    onClickWishlist={() => handleUndoWishlist(item)}
                    initialWishlistState={item.initialWishlistState}
                    type='cart'
                  />
                </div>
              </CSSTransition>
            );
          })}
        </TransitionGroup>
      </div>

      {/* Items container */}
      {cartData && cartData.items && cartData.items.length > 0 ? (
        <div className='flex flex-col relative'>
          {productLayout === 'table' ? (
            <TableLayout
              settings={{ ...settings, showDividers: true, removeItemBorders: true }}
              cartData={cartData}
            />
          ) : productLayout === 'grid' ? (
            <GridLayout
              settings={{ ...settings, showDividers: true, removeItemBorders: true }}
              cartData={cartData}
            />
          ) : (
            <StackedLayout
              settings={{
                ...settings,
                showDividers: blockSettings?.show_item_divider ?? true,
                removeItemBorders: true
              }}
              cartData={cartData}
              onQuantityChange={handleQuantityChange}
              onRemoveItem={handleRemoveItem}
            />
          )}

          {/* Loading Overlay */}
          {isGlobalCartLoading && (
            <div className='absolute inset-0 top-0 bg-white/80 flex items-center justify-center z-[100] w-full rounded-lg pointer-events-none'>
              <div className='flex items-center gap-3 pointer-events-auto'>
                <span className='b7--medium text-gray-600 animate-pulse'>Loading...</span>
                <span className='spinner spinner--md'></span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className='cart__summary text-center overflow-hidden '
          style={emptyPaddingStyle}
        >
          <p
            className={`bg-[#EDEDED] w-full rounded-[8px] py-[16px] px-[12px] ${getCurrentTextStyle(
              empty_cart_message_style,
              empty_cart_message_style_enable_responsive,
              empty_cart_message_style_mobile,
              empty_cart_message_style_tablet,
              empty_cart_message_style_ultrawide
            )} ${empty_cart_message_alignment}`}
          >
            {empty_cart_message}
          </p>
        </div>
      )}
    </div>
  );
};
